import dayjs from 'dayjs'
import { useState } from 'react'
import { useWireValue } from '@forminator/react-wire'
import ContentEditable from '@/components/contenteditable'
import { NUMBER_DISPLAY_TYPE } from '@/constants'
import * as actions from '@actions'
import * as store from '@store'
import useNavigate from '@hook/useNavigate'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { MdFileDownload } from 'react-icons/md'
import { createSyntheticDownload } from '@/utils'
import { toast } from 'react-toastify'
import { SelectRunsheetDialog } from '@components/collection/SelectRunsheetDialog'
import { UpsertRunsheet } from '@components/tract/UpsertRunsheet'
import { UpsertTract } from '../UpsertTract'
import { useWireState } from '@forminator/react-wire'
import useAbortableFetch from '@/hook/useAbortableFetch'

const MINERAL_CONVEYANCE = ['executive', 'bonus', 'delay', 'royalty']

/**
 * * Add table for address on party name and leave ppl to type in addresses
 * * Hide Lease columns for now
 * * Hide Encumbrances columns for now
 * * Whats NMA: Net mineral acres = total acres * percentage of the biggest of them
 * * Whats NRA: Net Royalty acres = total acres * percentage of royalty
 * * Hide Working Interest Owner
 * * Hide Overriding Royalty Interest Owners
 */
export const TractOwnership = () => {
    
    const [ownership, setOwnership] = useState({ parties: [], tract: {} })
    const { id } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [exportLoading, setExportLoading] = useState(false)
    const [showAdd, showHideAdd] = useState(false)
    const allRunsheets = useWireValue(store.runsheets)
    const [selectRunsheetDialogOpen, setSelectRunsheetDialogOpen] = useState(false)
    const [createRunsheetDialogOpen, setCreateRunsheetDialogOpen] = useState(false)
    const [runsheetOrTabId, setRunsheetOrTabId] = useState('')
    const [numberDisplayType, setNumberDisplayType] = useWireState(store.numberDisplayType)
    const navigate = useNavigate()
    
    const surfaceParties = ownership.parties.filter(p => p.conveyances.surface > 0)
    const mineralParties = ownership.parties.filter(p => p.conveyances.executive > 0
        || p.conveyances.bonus > 0 || p.conveyances.delay > 0 || p.conveyances.royalty > 0)
    
    const updateAddress = async (partyId, value) => {
        await actions.upsertPartyAddress(partyId, value)
        
        setOwnership(current => {
            const partyIndex = current.parties.findIndex(p => p.id === partyId)
            const updatedParties = [...current.parties]
            
            updatedParties[partyIndex] = {
                ...updatedParties[partyIndex],
                address: value,
            }
            
            return {
                ...current,
                parties: updatedParties,
            }
        })
    }
    
    const exportTract = async () => {
        setExportLoading(true)
        const res = await actions.exportTract(id, {
            runsheetId: ownership.runsheetId,
            runsheetTabId: ownership.runsheetTabId,
            formatType: numberDisplayType,
        })
        
        if (res)
            createSyntheticDownload(res.data, res.headers['content-type'], res.headers['content-name'])
        else
            toast.error('Failed to export Tract List, please try again.')
        
        setExportLoading(false)
    }
    
    const fetchOwnership = async id => {
        const ownershipData = await actions.findOwnershipFromTract(id,
            searchParams.get('runsheetTabId'), searchParams.get('runsheetId'))
        
        if (ownershipData.runsheetTabId) {
            setRunsheetOrTabId(ownershipData.runsheetTabId)
        } else if (ownershipData.runsheetId) {
            setRunsheetOrTabId(ownershipData.runsheetId)
        }
        
        setOwnership(ownershipData)
    }
    
    const navigateRunsheet = () => {
        if (ownership.runsheetId) {
            const search = ownership.runsheetTabId ? `?activeTab=${ownership.runsheetTabId}` : ''
            
            navigate(`/runsheet/${ownership.runsheetId}${search}`)
        } else {
            setSelectRunsheetDialogOpen(true)
        }
    }
    
    const handleToggle = () => {
        if (numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT) {
            setNumberDisplayType(NUMBER_DISPLAY_TYPE.DECIMAL)
        } else {
            setNumberDisplayType(NUMBER_DISPLAY_TYPE.PERCENT)
        }
    }
    
    const sumParties = (parties, conveyanceType) => 
        parties.map(p => p.conveyances[conveyanceType])
            .reduce((acc, value) => acc + value, 0)
    
    const handleRunsheetOrTabSelection = ev => {
        const value = ev.target.value
        
        setRunsheetOrTabId(value)
        
        const opt = ev.target.selectedOptions[0]
        const type = opt.dataset.type
        
        let runsheetId = value, runsheetTabId = ''
        
        if (type === 'tab') {
            runsheetTabId = value
            runsheetId = opt.dataset.runsheetId
        }
        
        setSearchParams(prev => {
            prev.set('runsheetId', runsheetId)
            prev.set('runsheetTabId', runsheetTabId)
            return prev
        })
    }
    
    useAbortableFetch(() => {
        return fetchOwnership(id)
    }, [id, searchParams.get('runsheetTabId'), searchParams.get('runsheetId')])
    
    // useAbortableFetch(() => {
    //     fetchOwnership(id)
    //     actions.fetchRunsheets()
    // }, [id])
    
    return (
        <>
            <div className="flex flex-col gap-4 p-4">
                <table className="rounded no-hover">
                    <thead>
                        <tr>
                            <th>Tract Name</th>
                            <th>Gross Acreage</th>
                            <th>Assigned To</th>
                            <th>Status</th>
                            <th>Status Date</th>
                            <th>Certification Date</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{ownership.tract.name}</td>
                            <td>{ownership.tract.grossAcreage?.toFixed(6)}</td>
                            <td>{ownership.tract.runsheets?.[0]?.assignedTo?.[0]?.firstName}</td>
                            <td>{ownership.tract.runsheets?.[0]?.status}</td>
                            <td>{ownership.tract.runsheets?.[0]?.statusDate
                                && dayjs(ownership.tract.runsheets[0].statusDate).format('MM/DD/YYYY')}</td>
                            <td>{ownership.tract.runsheets?.[0]?.certDate
                                && dayjs(ownership.tract.runsheets[0].certDate).format('MM/DD/YYYY')}</td>
                            <td>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => showHideAdd(true)}>
                                    Edit
                                </button>
                            </td>
                        </tr>
                        <tr>
                            <th colSpan="7">Tract Description</th>
                        </tr>
                        <tr>
                            <td className="border-0" colSpan="7">{ownership.tract.description}</td>
                        </tr>
                    </tbody>
                </table>
                <div className="flex items-center gap-2">
                    Ownership Information:
                    {!ownership.tract?.runsheets?.length || !ownership.tract?.runsheetTabs &&
                        <div>
                            <select className="select select-bordered select-sm"
                                value={runsheetOrTabId}
                                onChange={handleRunsheetOrTabSelection}>
                                {ownership.tract?.runsheets?.map(rs =>
                                    <option key={rs.id} value={rs.id} data-type="runsheet">{rs.name}</option>)}
                                {ownership.tract?.runsheetTabs?.map(rt =>
                                    <option key={rt.id} value={rt.id} data-type="tab" data-runsheet-id={rt.runsheet.id}>
                                        {rt.name} ({rt.runsheet.name})
                                    </option>)
                                }
                            </select>
                        </div>
                    }
                    <button
                        className="btn btn-primary"
                        onClick={() => navigateRunsheet()}>
                        Runsheet
                    </button>
                    <Link
                        className="btn btn-primary"
                        to={`/runsheet/${ownership.runsheetId}/title-breakdown?activeTab=${
                            ownership.runsheetTabId || '0'
                        }`}
                        disabled={!ownership?.runsheetId || !ownership?.parties.length}>
                        Title Chain
                    </Link>
                    <button className="btn btn-primary" onClick={exportTract}
                        disabled={exportLoading || !ownership?.parties.length}>
                        <MdFileDownload />Export
                    </button>
                    {(!!surfaceParties.length || !!mineralParties.length) && <button
                        className="btn btn-primary"
                        onClick={() => handleToggle()}>
                        {numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT ? 'Switch to Decimal' : 'Switch to Percent'}
                    </button>}
                </div>
                {!!surfaceParties.length && <div className="max-h-[244.5px] overflow-auto">
                    <table className="table table-sm table-nowrap card-table table-pin-rows">
                        <thead>
                            <tr>
                                <th className="w-[300px]">Surface Owners</th>
                                <th className="w-40">Surface</th>
                                <th className="w-40">Net Acreage</th>
                                <th>Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT ? surfaceParties.map(p => {
                                return (
                                    <tr key={p.id}>
                                        <td>{p.name}</td>
                                        <td>{(p.conveyances.surface * 100).toFixed(6)}%</td>
                                        <td>{((ownership.tract.grossAcreage || 0)
                                            * Math.max(0, p.conveyances.surface)).toFixed(6)}</td>
                                        <td>
                                            <ContentEditable
                                                type="textarea"
                                                grow="sm"
                                                onEditEnd={value => updateAddress(p.id, value)}
                                                value={p.address} />
                                        </td>
                                    </tr>
                                )
                            }) : surfaceParties.map(p => {
                                return (
                                    <tr key={p.id}>
                                        <td>{p.name}</td>
                                        <td>{(p.conveyances.surface).toFixed(8)}</td>
                                        <td>{((ownership.tract.grossAcreage || 0)
                                            * Math.max(0, p.conveyances.surface)).toFixed(6)}</td>
                                        <td>
                                            <ContentEditable
                                                type="textarea"
                                                grow="sm"
                                                onEditEnd={value => updateAddress(p.id, value)}
                                                value={p.address} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            {numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT ? (
                                <tr>
                                    <td>Total</td>
                                    <td>
                                        {(sumParties(surfaceParties, 'surface') * 100).toFixed(6)}%
                                    </td>
                                    <td colSpan={2}>
                                        {((ownership.tract.grossAcreage || 0) *
                                            sumParties(surfaceParties, 'surface')).toFixed(6)}
                                    </td>
                                </tr>
                                
                            ) : (
                                <tr>
                                    <td>Total</td>
                                    <td>
                                        {(sumParties(surfaceParties, 'surface')).toFixed(8)}
                                    </td>
                                    <td colSpan={2}>
                                        {((ownership.tract.grossAcreage || 0) *
                                            sumParties(surfaceParties, 'surface')).toFixed(6)}
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>}
                {!!mineralParties.length && <div className="max-h-[244.5px] overflow-auto">
                    <table className="table table-sm table-nowrap card-table table-pin-rows">
                        <thead>
                            <tr>
                                <th className="w-[300px]">Mineral Owners</th>
                                <th className="w-28">Executive</th>
                                <th className="w-28">Bonus</th>
                                <th className="w-28">Delay</th>
                                <th className="w-28">Royalty</th>
                                <th className="w-28"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Net Mineral Acres">
                                    NMA
                                </th>
                                <th className="w-28"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Net Royalty Acres">
                                    NRA
                                </th>
                                <th>Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT ? mineralParties.map(p => {
                                const highestConveyance = Math.max(...MINERAL_CONVEYANCE.map(m => p.conveyances[m]))
                                
                                return (
                                    <tr key={p.id}>
                                        <td>{p.name}</td>
                                        <td>{Math.max(0, p.conveyances.executive * 100).toFixed(6)}%</td>
                                        <td>{Math.max(0, p.conveyances.bonus * 100).toFixed(6)}%</td>
                                        <td>{Math.max(0, p.conveyances.delay * 100).toFixed(6)}%</td>
                                        <td>{Math.max(0, p.conveyances.royalty * 100).toFixed(6)}%</td>
                                        <td>{((ownership.tract.grossAcreage || 0) * highestConveyance).toFixed(6)}</td>
                                        <td>
                                            {((ownership.tract.grossAcreage || 0) * p.conveyances.royalty).toFixed(6)}
                                        </td>
                                        <td>
                                            <ContentEditable
                                                type="textarea"
                                                grow="sm"
                                                onEditEnd={value => updateAddress(p.id, value)}
                                                value={p.address} />
                                        </td>
                                    </tr>
                                )
                            }) : mineralParties.map(p => {
                                const highestConveyance = Math.max(...MINERAL_CONVEYANCE.map(m => p.conveyances[m]))
                                
                                return (
                                    <tr key={p.id}>
                                        <td>{p.name}</td>
                                        <td>{Math.max(0, p.conveyances.executive).toFixed(8)}</td>
                                        <td>{Math.max(0, p.conveyances.bonus).toFixed(8)}</td>
                                        <td>{Math.max(0, p.conveyances.delay).toFixed(8)}</td>
                                        <td>{Math.max(0, p.conveyances.royalty).toFixed(8)}</td>
                                        <td>{((ownership.tract.grossAcreage || 0) * highestConveyance).toFixed(6)}</td>
                                        <td>
                                            {((ownership.tract.grossAcreage || 0) * p.conveyances.royalty).toFixed(6)}
                                        </td>
                                        <td>
                                            <ContentEditable
                                                type="textarea"
                                                grow="sm"
                                                onEditEnd={value => updateAddress(p.id, value)}
                                                value={p.address} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            {numberDisplayType === NUMBER_DISPLAY_TYPE.PERCENT ? (
                                <tr>
                                    <td>Total</td>
                                    <td>
                                        {(sumParties(mineralParties, 'executive') * 100).toFixed(6)}%
                                    </td>
                                    <td>
                                        {(sumParties(mineralParties, 'bonus') * 100).toFixed(6)}%
                                    </td>
                                    <td>
                                        {(sumParties(mineralParties, 'delay') * 100).toFixed(6)}%
                                    </td>
                                    <td>
                                        {(sumParties(mineralParties, 'royalty') * 100).toFixed(6)}%
                                    </td>
                                    <td>
                                        {((ownership.tract.grossAcreage || 0) *
                                        (mineralParties.map(p => 
                                            Math.max(...MINERAL_CONVEYANCE.map(m => p.conveyances[m])))
                                            .reduce((acc, value) => acc + value, 0))).toFixed(6)}
                                    </td>
                                    <td colSpan={2}>
                                        {((ownership.tract.grossAcreage || 0) *
                                            sumParties(mineralParties, 'royalty')).toFixed(6)}
                                    </td>
                                </tr>
                            ) : (
                                <tr>
                                    <td>Total</td>
                                    <td>
                                        {sumParties(mineralParties, 'executive').toFixed(8)}
                                    </td>
                                    <td>
                                        {sumParties(mineralParties, 'bonus').toFixed(8)}
                                    </td>
                                    <td>
                                        {sumParties(mineralParties, 'delay').toFixed(8)}
                                    </td>
                                    <td>
                                        {sumParties(mineralParties, 'royalty').toFixed(8)}
                                    </td>
                                    <td>
                                        {((ownership.tract.grossAcreage || 0) *
                                        (mineralParties.map(p => 
                                            Math.max(...MINERAL_CONVEYANCE.map(m => p.conveyances[m])))
                                            .reduce((acc, value) => acc + value, 0))).toFixed(6)}
                                    </td>
                                    <td colSpan={2}>
                                        {((ownership.tract.grossAcreage || 0) *
                                            sumParties(mineralParties, 'royalty')).toFixed(6)}
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>}
            </div>
            {showAdd && (
                <UpsertTract
                    id="create-tract"
                    showAdd={showAdd}
                    showHideAdd={showHideAdd}
                    editTract={ownership.tract}
                    onSuccess={() => fetchOwnership(id)} />
            )}
            <SelectRunsheetDialog
                runsheets={allRunsheets}
                open={selectRunsheetDialogOpen}
                setOpen={setSelectRunsheetDialogOpen}
                setCreateRunsheetDialogOpen={setCreateRunsheetDialogOpen}
                confirmButtonText="Assign"
                showTabs={true}
                onSelectRunsheet={async runsheetOrTab => {
                    if (runsheetOrTab) {
                        
                        if (runsheetOrTab.isTab) {
                            await actions.updateRunsheetTab(runsheetOrTab.id, { tractId: ownership.tract.id })
                        } else {
                            await actions.assignTractsToRunsheet(runsheetOrTab.id, ownership.tract.id)
                        }
                        
                        await fetchOwnership(id)
                    }
                }} />
            
            <UpsertRunsheet
                showAdd={createRunsheetDialogOpen}
                showHideAdd={setCreateRunsheetDialogOpen}
                redirect={false}
                runsheetSaved={async runsheet => {
                    if (runsheet) {
                        allRunsheets.unshift(runsheet)
                        await actions.assignTractsToRunsheet(runsheet.id, ownership.tract.id)
                        await fetchOwnership(id)
                    }
                    
                    setCreateRunsheetDialogOpen(false)
                }} />
        </>
    )
}
